import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import { GroupOption } from '@/types/metrics'

dayjs.extend(isSameOrBefore)

export const convertToDate = (date: string, isStartDate = true) => {
  const today = new Date()
  let value = date !== 'start' && date !== 'end' ? new Date(date) : today

  if (isStartDate) {
    if (date === 'start') {
      value.setDate(value.getDate() - 14)
    }
    value.setHours(0, 0, 0)
  } else {
    // We keep the time as current time for when the date is "today" so that we can keep track of the hours
    if (date === 'end' || dayjs().isSame(value, 'day')) {
      value = today
    } else {
      value.setHours(23, 59, 59)
    }
  }
  return value
}

export const formatDate = (date: Date) =>
  dayjs(date).format('YYYY-MM-DDTHH:mm:ss')

export const getNumberOfDaysBetweenDates = (start: Date, end: Date) => {
  const oneDay = 1000 * 60 * 60 * 24
  const diffInTime = end.getTime() - start.getTime()
  const diffInDays = Math.round(diffInTime / oneDay)
  return diffInDays
}

export const getEndDate = (startDate: Date, endDate: Date) => {
  if (getNumberOfDaysBetweenDates(startDate, endDate) > 6) {
    const newEndDate = new Date(startDate)
    newEndDate.setDate(newEndDate.getDate() + 6)
    newEndDate.setHours(23, 59, 59)
    return newEndDate
  }
  return null
}

export const getIsEnabled = (
  startDate: Date,
  endDate: Date,
  groupedBy: GroupOption,
) => {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  tomorrow.setHours(0, 0, 0, 0)

  let enabled =
    startDate < tomorrow && endDate >= startDate && endDate < tomorrow

  if (groupedBy === 'hour') {
    const dayAfterEndValidDate = new Date(startDate)
    dayAfterEndValidDate.setDate(dayAfterEndValidDate.getDate() + 7)
    dayAfterEndValidDate.setHours(0, 0, 0, 0)
    enabled = enabled && endDate < dayAfterEndValidDate
  }

  return enabled
}

export const getConvertedDatesByGroup = ({
  startDate,
  endDate,
  groupedBy,
}: {
  startDate?: Date
  endDate?: Date
  groupedBy: GroupOption
}) => {
  switch (groupedBy) {
    case 'month':
      return {
        startDate: startDate
          ? new Date(startDate.getFullYear(), startDate.getMonth(), 1)
          : undefined,
        endDate: endDate
          ? new Date(
              endDate.getFullYear(),
              endDate.getMonth() + 1,
              0,
              23,
              59,
              59,
            )
          : endDate,
      }
    case 'year':
      return {
        startDate: startDate ? new Date(startDate.getFullYear(), 0) : undefined,
        endDate: endDate
          ? new Date(endDate.getFullYear(), 11, 31, 23, 59, 59)
          : undefined,
      }
    default:
      return {
        startDate,
        endDate,
      }
  }
}

export function getYesterday() {
  // Use consistent Mountain Time (America/Denver) timezone
  const mountainTimezone = 'America/Denver'

  // Get current date parts in Mountain Time
  const mtFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: mountainTimezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  })

  const now = new Date()
  const mtParts = mtFormatter.formatToParts(now)

  // Extract year, month, day from the formatter parts
  const mtYear = parseInt(
    mtParts.find((part) => part.type === 'year')?.value || '0',
    10,
  )
  const mtMonth =
    parseInt(mtParts.find((part) => part.type === 'month')?.value || '0', 10) -
    1 // 0-indexed month
  const mtDay = parseInt(
    mtParts.find((part) => part.type === 'day')?.value || '0',
    10,
  )

  // To properly handle DST, we'll create a date in Mountain timezone and then
  // convert it to UTC, rather than using a fixed offset

  // First create a string representing midnight in Mountain Time
  const mtMidnightStr = `${mtYear}-${String(mtMonth + 1).padStart(
    2,
    '0',
  )}-${String(mtDay).padStart(2, '0')}T00:00:00`

  // Format options for getting the exact offset on this date
  const options = { timeZone: mountainTimezone, timeZoneName: 'short' }

  // Create a temporary date to determine if DST is in effect
  const tempDate = new Date(`${mtMidnightStr}Z`)
  // @ts-expect-error -- DateTimeFormatOptions isn't an available type?
  const isDST = new Intl.DateTimeFormat('en-US', options)
    .format(tempDate)
    .includes('MDT')

  // Apply the correct offset based on whether DST is in effect
  // Mountain Standard Time is UTC-7, Mountain Daylight Time is UTC-6
  const utcHourOffset = isDST ? 6 : 7

  // Create date object for today at midnight in Mountain Time
  const mtMidnight = new Date(
    Date.UTC(mtYear, mtMonth, mtDay, utcHourOffset, 0, 0, 0),
  )

  // Subtract one day to get yesterday
  const mtYesterday = new Date(mtMidnight)
  mtYesterday.setUTCDate(mtYesterday.getUTCDate() - 1)

  return mtYesterday
}

export function convertRangeValueToDates(rangeValue: string) {
  const startDate = getYesterday()
  const endDate = getYesterday()
  endDate.setHours(23, 59, 59, 999) // Some endpoints need the end date to be inclusive
  switch (rangeValue) {
    case '1day':
      break
    case '2days':
      startDate.setDate(startDate.getDate() - 1)
      break
    case '3days':
      startDate.setDate(startDate.getDate() - 2)
      break
    case '1week':
      startDate.setDate(startDate.getDate() - 6)
      break
    case '2weeks':
      startDate.setDate(startDate.getDate() - 13)
      break
    case '1month':
      startDate.setMonth(startDate.getMonth() - 1)
      break
    default:
      break
  }

  return {
    startDate,
    endDate,
  }
}
